import './global.css';
import './lib/i18n';
import { rumRootOptions, setSiteGroupCode } from './observability/datadog';
import { DatadogUserSync, DatadogFacilitySync } from './observability/DatadogSync';
import { StrictMode, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { useAtomValue, getDefaultStore } from 'jotai';
import { queryClient } from './lib/queryClient';
import { router } from './router';
import {
  createAppTheme,
  themeTokensFromConfig,
  darkModePolicyFromConfig,
  resolveThemeMode,
} from '@dh/theme-core';
import { applyLanguage } from './lib/i18n';
import { lookupService } from './services/lookupService';
import { setSystemConfig } from './lib/systemConfig';
import { useSiteGroupExperience } from './hooks/useSiteGroupExperience';
import { siteGroupExperienceAtom } from './store/siteGroupExperienceAtom';
import { bootstrapSiteGroupFromUrl } from './lib/bootstrapSiteGroup';
import { bootstrapAuth } from './lib/authBootstrap';
import { selectedFacilityAtom } from './store/facilityAtom';
import { currentLangAtom } from './store/langAtom';
import { useSetAtom } from 'jotai';
import { bootstrapParagraphCodeMode } from './lib/paragraphCodeMode';
import { ParagraphCodeModeBadge } from './components/common/ParagraphCodeModeBadge';
import { AdminAnnotationBridge } from './components/AdminAnnotationBridge';
import { RemoteAccessBootstrap } from './components/RemoteAccessBootstrap';
import { RemoteAccessBanner } from './components/RemoteAccessBanner';
import { DesktopInputFocusManager } from './components/common/DesktopInputFocusManager';

bootstrapParagraphCodeMode();
bootstrapSiteGroupFromUrl();
bootstrapAuth();

// Earliest possible tag � covers localStorage rehydration and ?sitegroup= URL param,
// both of which are resolved synchronously by bootstrapSiteGroupFromUrl above.
const _initialFacility = getDefaultStore().get(selectedFacilityAtom);
if (_initialFacility?.id) setSiteGroupCode(_initialFacility.id);

// Third-party localisation note:
// MUI date/time pickers require a <LocalizationProvider adapter={AdapterDayjs} adapterLocale={lang}>
// wrapper to display month/day names in the user's language. Import and wrap when date pickers are added.
// MUI DataGrid column headers use the `localeText` prop on <DataGrid> � pass a MUI locale object there.
// Neither of these is controlled by l_paragraph_config; they receive the locale code directly.

const VALIDATE_STATE_KEY = 'facility_validated_at';
const VALIDATE_STATE_TTL_MS = 24 * 60 * 60 * 1000;
// Evaluated once at module load � unaffected by React re-renders or StrictMode double-mount.
// Skipped for Engage: facility is token-driven there, not from the normal selection flow.
const VALIDATE_STATE_DUE =
  !window.location.pathname.startsWith('/engage') &&
  Date.now() - Number(localStorage.getItem(VALIDATE_STATE_KEY) ?? 0) >= VALIDATE_STATE_TTL_MS;

/** Emotion caches � one for LTR, one for RTL (with stylis-plugin-rtl) */
const ltrCache = createCache({ key: 'mui' });
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

/**
 * Resolves the active facility from l_marketing_domain on every app load.
 * Sends the current portal hostname; the API also checks X-Forwarded-Host and
 * the Referer header server-side, so a single request covers all domain sources.
 *
 * Priority (highest first):
 *   1. ?sitegroup= URL param � handled synchronously by bootstrapSiteGroupFromUrl.
 *   2. Marketing domain match � current hostname (+ server-side header checks).
 *   3. Single-facility auto-select � handled by SiteGroupBootstrap below.
 *
 * When a match is found the facility atom is set unconditionally, overriding any
 * stale value persisted in localStorage from a previous session on a different domain.
 */
function MarketingDomainBootstrap() {
  const setFacility = useSetAtom(selectedFacilityAtom);

  useQuery({
    queryKey: ['marketingDomain'],
    queryFn: async () => {
      // URL-param facility takes priority � don't override it.
      if (sessionStorage.getItem('pp_facility_from_url_param')) return null;

      const result = await lookupService.getMarketingDomain(window.location.hostname);
      if (result?.siteGroupCode) {
        setFacility({ id: result.siteGroupCode, name: result.siteGroupCode });
      }
      return result;
    },
    staleTime: Infinity,
    retry: false,
  });

  return null;
}

/**
 * Prefetches the site group list at app init. If exactly one facility is
 * returned and no facility is already selected (either from localStorage or a
 * ?sitegroup= URL param consumed by bootstrapSiteGroupFromUrl), auto-selects
 * it so the user is taken straight to /home, bypassing facility selection.
 */
function SiteGroupBootstrap() {
  const setFacility = useSetAtom(selectedFacilityAtom);

  useQuery({
    queryKey: ['siteGroups'],
    queryFn: async () => {
      const groups = await lookupService.getSiteGroups();
      if (groups.length === 1) {
        const only = groups[0]!;
        setFacility(prev =>
          prev ? prev : { id: only.siteGroupCode, name: only.description }
        );
      }
      return groups;
    },
    staleTime: Infinity,
  });

  return null;
}

/** Fetches system config immediately for all users � endpoint is anonymous. */
function SystemConfigBootstrap() {
  useQuery({
    queryKey: ['lookup', 'systemConfig'],
    queryFn: async () => {
      const data = await lookupService.getSystemConfig();
      setSystemConfig(data);
      return data;
    },
    staleTime: 15 * 60 * 1000,
  });
  return null;
}

/**
 * Once per day, validates that the stored facility and language are still
 * active. On failure clears the affected atom so the user is prompted to
 * re-select on their next interaction.
 */
function ValidateStateBootstrap() {
  const facility = useAtomValue(selectedFacilityAtom);
  const setFacility = useSetAtom(selectedFacilityAtom);
  const lang = useAtomValue(currentLangAtom);

  useQuery({
    queryKey: ['validateState'],
    enabled: VALIDATE_STATE_DUE && !!facility,
    queryFn: async () => {
      localStorage.setItem(VALIDATE_STATE_KEY, String(Date.now()));

      const result = await lookupService.validateSessionState({
        facilityId: facility!.id,
        languageCode: lang,
      });

      if (!result.facility.valid) setFacility(null);
      if (!result.language.valid) applyLanguage('en');

      return result;
    },
    staleTime: Infinity,
    retry: false,
  });

  return null;
}

/** Fetches the selected facility's experience (theme/feature overrides). No-op if none selected. */
function SiteGroupExperienceBootstrap() {
  useSiteGroupExperience();
  return null;
}

function App() {
  const lang = useAtomValue(currentLangAtom);
  const direction = lang.startsWith('ar') ? 'rtl' : 'ltr';
  const experience = useAtomValue(siteGroupExperienceAtom);

  // Follow OS prefers-color-scheme unless facility forces light/dark via PPThemeDarkMode.
  // noSsr: avoid a false "light" flash on the first client paint before the media query resolves.
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const mode = useMemo(() => {
    const policy = darkModePolicyFromConfig(experience?.configOverride);
    return resolveThemeMode(policy, prefersDark);
  }, [experience?.configOverride, prefersDark]);

  const theme = useMemo(
    () => createAppTheme(direction, themeTokensFromConfig(experience?.configOverride), mode),
    [direction, experience, mode],
  );

  // Align non-MUI CSS / native controls with the effective mode (scrollbars, form UA styling).
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const cache = direction === 'rtl' ? rtlCache : ltrCache;

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <DatadogUserSync />
          <DatadogFacilitySync />
          <MarketingDomainBootstrap />
          <SiteGroupBootstrap />
          <SystemConfigBootstrap />
          <SiteGroupExperienceBootstrap />
          <ValidateStateBootstrap />
          <AdminAnnotationBridge />
          <RemoteAccessBootstrap />
          <RemoteAccessBanner />
          <DesktopInputFocusManager />
          <RouterProvider router={router} />
          <ParagraphCodeModeBadge />
        </QueryClientProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root, rumRootOptions).render(
  <StrictMode>
    <App />
  </StrictMode>
);
